import { AppState, useAuth0 } from '@auth0/auth0-react'
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'

import posthog from 'posthog-js'
import React, { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { Loading } from '../components/Loading'
import {
  AUTH0_API_SCOPE,
  cleanupAfterAuthDecline,
  getPostDeclineLogoutReturnTo,
  isNativeApp,
  markSignupFlowPending,
} from '../config/auth0'
import { prepareLogout } from '../utils/logout'

type AuthStateContextType = {
  getToken: () => string
  setRedirectUrl: (url: string) => void
  getRedirectUrl: () => string
}
export const AuthStateContext: AuthStateContextType = {
  getRedirectUrl: () => {
    throw new Error('AuthStateContext must be used within a AuthStateProvider')
  },
  getToken: () => {
    throw new Error('AuthStateContext must be used within a AuthStateProvider')
  },
  setRedirectUrl: () => {
    throw new Error('AuthStateContext must be used within a AuthStateProvider')
  },
}

type AuthTokenContextValue = {
  token: string
  /** True when logged-out, or when a JWT has been fetched for the current session */
  isTokenReady: boolean
}

const AuthTokenContext = React.createContext<AuthTokenContextValue>({
  isTokenReady: true,
  token: '',
})

export const useAuthToken = (): AuthTokenContextValue => useContext(AuthTokenContext)

type AuthStateProviderProps = { children: React.ReactNode }

export const AuthStateProvider = ({ children }: AuthStateProviderProps): JSX.Element => {
  const isMobileDevice = isNativeApp()
  const domain = process.env.REACT_APP_AUTH0_DOMAIN || 'scrubstr.us.auth0.com'

  const [authToken, setAuthToken] = React.useState<string>('')
  const [tokenLoading, setTokenLoading] = React.useState(false)
  const [redirectUrl, setRedirectUrl] = React.useState<string>('')
  const history = useHistory()

  const { user, isAuthenticated, getAccessTokenSilently, handleRedirectCallback, logout } = useAuth0()

  useEffect(() => {
    if (isMobileDevice) {
      CapApp.addListener('appUrlOpen', async ({ url }) => {
        if (url.includes('state') && (url.includes('code') || url.includes('error'))) {
          const destination = AuthStateContext.getRedirectUrl() || '/profile'

          if (url.includes('error=')) {
            cleanupAfterAuthDecline()
            AuthStateContext.setRedirectUrl('')
            history.push('/hospitals')
            await Browser.close()
            void logout({
              logoutParams: {
                returnTo: getPostDeclineLogoutReturnTo(),
              },
              async openUrl(logoutUrl) {
                await Browser.open({
                  url: logoutUrl,
                  windowName: '_self',
                })
              },
            })
            return
          }

          try {
            const result = await handleRedirectCallback(url)
            if ((result.appState as AppState & { fromSignup?: boolean })?.fromSignup) {
              markSignupFlowPending()
            }
            history.push(destination)
            AuthStateContext.setRedirectUrl('')
          } catch {
            cleanupAfterAuthDecline()
            history.push('/hospitals')
            AuthStateContext.setRedirectUrl('')
            void logout({
              logoutParams: {
                returnTo: getPostDeclineLogoutReturnTo(),
              },
              async openUrl(logoutUrl) {
                await Browser.open({
                  url: logoutUrl,
                  windowName: '_self',
                })
              },
            })
          }
        }

        await Browser.close()
      })
    }
  }, [handleRedirectCallback, history, logout])

  useEffect(() => {
    if (isAuthenticated && user) {
      posthog.identify(user.sub || user.user_id, {
        email: user.email,
        role: 'user',
      })

      posthog.capture('user_authenticated', {
        email: user.email,
        user_id: user.sub || user.user_id,
      })

      setTokenLoading(true)
      // Management API scope only — full OIDC scopes here can yield a token the API rejects.
      getAccessTokenSilently({
        authorizationParams: {
          audience: `https://${domain}/api/v2/`,
          scope: AUTH0_API_SCOPE,
        },
      })
        .then((accessToken) => {
          setAuthToken(accessToken)
          setTokenLoading(false)
        })
        .catch(() => {
          // Offline / expired refresh token / network error: clear local session so we
          // don't sit on the loading screen forever (isAuthenticated can still be true
          // briefly while the cached Auth0 user exists).
          prepareLogout(user?.sub)
          setAuthToken('')
          setTokenLoading(false)
          void logout({ openUrl: false })
        })
    } else {
      setAuthToken('')
      setTokenLoading(false)
    }
  }, [isAuthenticated, user, getAccessTokenSilently, domain, logout])

  AuthStateContext.getToken = () => authToken
  AuthStateContext.setRedirectUrl = (url: string) => {
    setRedirectUrl(url)
  }
  AuthStateContext.getRedirectUrl = () => redirectUrl

  // Only block the UI while a token request is in flight — never gate on !authToken,
  // or a failed fetch leaves the app stuck on Loading forever.
  const showLoading = isAuthenticated && !!user && tokenLoading
  const isTokenReady = !(isAuthenticated && !!user) || !!authToken

  return (
    <AuthTokenContext.Provider value={{ isTokenReady, token: authToken }}>
      {showLoading ? (
        <div className="bg-light-blue flex justify-center items-center min-h-screen relative">
          <Loading />
        </div>
      ) : (
        children
      )}
    </AuthTokenContext.Provider>
  )
}
